import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PowerOff, Search } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/ui/PageHeader.js";
import { DataTable, type Column } from "@/components/ui/DataTable.js";
import { api } from "@/lib/api.js";
import { relativeTime } from "@/lib/format.js";
import { useAuthStore } from "@/lib/auth.js";
import type { ActiveSession } from "@bo/shared";

export function SessionsPage() {
  const [q, setQ] = useState("");
  const qc = useQueryClient();
  const canKill = useAuthStore((s) => s.hasPermission("session.kill"));

  const { data, isLoading } = useQuery({
    queryKey: ["sessions", "active", q],
    queryFn: () => {
      const filtro = q ? `&q=${encodeURIComponent(q)}` : "";
      return api.get<{ items: ActiveSession[] }>(`/sessions/active?pageSize=100${filtro}`);
    },
    refetchInterval: 20_000,
  });

  const kill = useMutation({
    mutationFn: (s: ActiveSession) =>
      api.post<{ message?: string }>("/sessions/kill", {
        targetEmail: s.email,
        targetGinaUsuarioId: s.idusuario,
        targetIdcompany: s.idcompany ?? undefined,
        reason: "Forzado desde backoffice",
      }),
    onSuccess: (res) => {
      toast.success(res?.message ?? "Sesión cerrada correctamente");
      qc.invalidateQueries({ queryKey: ["sessions", "active"] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const columns: Column<ActiveSession>[] = [
    {
      header: "Email",
      render: (r) => <span className="mono text-xs">{r.email}</span>,
    },
    { header: "Nombre", render: (r) => r.fullName ?? "—" },
    { header: "Company", render: (r) => r.companyName ?? (r.idcompany ?? "—") },
    { header: "IP", render: (r) => <span className="mono text-xs">{r.ip ?? "—"}</span> },
    {
      header: "Duración",
      render: (r) => (
        <span className={r.durationMinutes > 720 ? "text-rose-600" : ""}>
          {r.durationMinutes < 60
            ? `${r.durationMinutes} min`
            : `${Math.floor(r.durationMinutes / 60)}h ${r.durationMinutes % 60}m`}
        </span>
      ),
    },
    { header: "Login", render: (r) => relativeTime(r.loginAt) },
    {
      header: "",
      width: "120px",
      render: (r) =>
        canKill ? (
          <button
            className="btn-danger"
            onClick={() => {
              if (confirm(`Cerrar sesión de ${r.email}?`)) kill.mutate(r);
            }}
            disabled={kill.isPending}
          >
            <PowerOff size={12} className="mr-1" />
            Cerrar
          </button>
        ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sesiones activas"
        description="Agentes actualmente logueados en el core de Gina."
      />
      <div className="flex gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
          />
          <input
            className="input pl-9"
            placeholder="Buscar por email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="ml-auto text-xs text-slate-500 flex items-center">
          Auto-refresh 20s
        </div>
      </div>
      <DataTable
        columns={columns}
        rows={data?.items}
        loading={isLoading}
        getKey={(r) => `${r.idusuario}-${r.loginAt}`}
        empty="No hay sesiones activas ahora mismo."
      />
      <p className="text-xs text-slate-400">
        Cerrar fuerza el reinicio de sesión del agente en el core (REINICIARSESION) y elimina
        su registro de sesión activa. Útil para liberar sesiones fantasma que impiden re-loguearse.
        Toda acción queda auditada.
      </p>
    </div>
  );
}
